
import { useState } from "react";

import {
  Ban,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Mail,
  MapPin,
  Phone,
  UserCheck,
  XCircle,
} from "lucide-react";

import { mapCandidateDetails } from "@/modules/hrPortal/utils/candidateDetailsMapper";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";

type CandidateData =
  ReturnType<typeof mapCandidateDetails>["candidateData"];

interface CandidateApplicationSummary {
  id: string;
  jobTitle: string;
  appliedAt: string;
}

interface CandidateSummaryCardProps {
  candidateData: CandidateData;
  currentStatus: string;
  statusUpdating: boolean;
  onNextStatus: (status: string) => Promise<void>;
  onReject: () => void;
  onBlacklist: () => void;
  applications?: CandidateApplicationSummary[];
  currentApplicationId?: string;
  onApplicationSelect?: (applicationId: string) => void;

  /**
   * SH-22:
   * Controls visibility of Hiring Decisions actions.
   * Defaults to true to preserve existing behavior.
   */
  canAccessHiringDecisions?: boolean;
  /** Allows global blacklist actions even when another team owns the candidate. */
  canAccessGlobalBlacklist?: boolean;
}

const NEXT_STATUS_MAP: Record<string, string> = {
  "Under Review": "Shortlisted",
};

const NEXT_STATUS_LABELS: Record<string, string> = {
  "Under Review": "Shortlist",
  Shortlisted: "Shortlisted",
  "Shortlisted for HR Round": "Shortlisted",
  "Shortlisted for Technical Round": "Shortlisted",
  "Shortlisted for Management Round": "Shortlisted",
};

export default function CandidateSummaryCard({
  candidateData,
  currentStatus,
  statusUpdating,
  onNextStatus,
  onReject,
  onBlacklist,
  applications = [],
  currentApplicationId = "",
  onApplicationSelect,
  canAccessHiringDecisions = true,
  canAccessGlobalBlacklist,
}: CandidateSummaryCardProps) {
  const [showApplications, setShowApplications] = useState(false);
  const canShowGlobalBlacklist =
    canAccessGlobalBlacklist ?? canAccessHiringDecisions;

  const nextStatus = NEXT_STATUS_MAP[currentStatus];
  const nextStatusLabel = NEXT_STATUS_LABELS[currentStatus];

  const showNextAction =
    Boolean(nextStatusLabel) &&
    currentStatus !== "Selected" &&
    currentStatus !== "Rejected" &&
    currentStatus !== "Blacklisted";

  const otherApplications = applications
    .filter((item) => item.id !== currentApplicationId)
    .sort(
      (a, b) =>
        new Date(b.appliedAt).getTime() -
        new Date(a.appliedAt).getTime()
    );

  const handleNextStatus = () => {
    if (
      currentStatus !== "Under Review" ||
      !nextStatus
    ) {
      return;
    }

    void onNextStatus(nextStatus);
  };

  const formatApplicationDate = (date: string) => {
    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const candidateName = candidateData.name?.trim() || "N/A";

  const initials =
    candidateName === "N/A"
      ? "NA"
      : candidateName
        .split(/\s+/)
        .map((name) => name[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

  const statusClass =
    currentStatus === "Rejected"
      ? "border-red-200 bg-red-50 text-red-700"
      : currentStatus === "Blacklisted"
        ? "border-red-200 bg-red-50 text-red-800"
        : currentStatus === "Selected"
          ? "border-green-200 bg-green-50 text-green-700"
          : currentStatus === "Shortlisted"
            ? "border-blue-200 bg-blue-50 text-blue-700"
            : "border-slate-200 bg-slate-100 text-slate-700";

  const details = [
    {
      label: "Email",
      value: candidateData.email,
      Icon: Mail,
      breakAll: true,
    },
    {
      label: "Phone",
      value: candidateData.phone,
      Icon: Phone,
      breakAll: false,
    },
    {
      label: "Location",
      value: candidateData.location,
      Icon: MapPin,
      breakAll: false,
    },
    {
      label: "Experience",
      value: candidateData.experience
        ? `${candidateData.experience} experience`
        : "",
      Icon: Briefcase,
      breakAll: false,
    },
  ];

  return (
    <Card className="min-w-0 overflow-visible rounded-2xl border-slate-200/80 bg-white shadow-sm">
      <CardContent className="min-w-0 p-4 sm:p-5 lg:p-6">
        {/* Candidate profile */}
        <div className="min-w-0">
          {/* Avatar, name, status and Applications */}
          <div className="flex min-w-0 items-start gap-3 sm:gap-4">
            {/* Responsive avatar */}
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-lg font-bold text-white shadow-sm sm:h-16 sm:w-16 sm:text-xl lg:h-20 lg:w-20 lg:text-2xl">
              {initials}
            </div>

            {/* Name and status */}
            <div className="min-w-0 flex-1 pt-0.5 sm:pt-1">
              <h2 className="break-words text-base font-bold leading-tight text-slate-900 sm:text-xl lg:text-2xl">
                {candidateName}
              </h2>

              <Badge
                variant="outline"
                className={`mt-1 rounded-full px-2 py-0.5 text-[10px] font-semibold sm:mt-2 sm:text-xs ${statusClass}`}
              >
                {currentStatus || "Unknown"}
              </Badge>
            </div>

            {/* Applications dropdown: top-right */}
            {otherApplications.length > 0 &&
              onApplicationSelect && (
                <div className="relative z-20 ml-auto shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1 rounded-lg px-2 text-[10px] sm:h-9 sm:gap-2 sm:px-3 sm:text-sm"
                    aria-expanded={showApplications}
                    onClick={() =>
                      setShowApplications((current) => !current)
                    }
                  >
                    <span>Applications</span>

                    {showApplications ? (
                      <ChevronUp className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                    )}
                  </Button>


                  {/* Applications dropdown */}
                  {showApplications && (
                    <div className="absolute right-0 top-full z-30 mt-1.5 w-56 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg sm:w-64">
                      <div className="border-b border-slate-100 bg-slate-50 px-3 py-2">
                        <p className="text-[11px] font-semibold text-slate-700 sm:text-xs">
                          Other Applications ({otherApplications.length})
                        </p>
                      </div>

                      <div className="max-h-[186px] overflow-y-auto overscroll-contain sm:max-h-[320px]">
                        {otherApplications.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className="flex w-full min-w-0 items-start gap-2 border-b border-slate-50 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-slate-50"
                            onClick={() => {
                              onApplicationSelect?.(item.id);
                              setShowApplications(false);
                            }}
                          >
                            <Briefcase className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600 sm:h-4 sm:w-4" />

                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs font-medium text-slate-900 sm:text-sm">
                                {item.jobTitle || "Untitled position"}
                              </span>

                              <span className="mt-0.5 block text-[10px] text-slate-500 sm:text-xs">
                                Applied: {formatApplicationDate(item.appliedAt)}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              )}
          </div>

          {/* Applied for: below avatar row on mobile, aligned with content on desktop */}
          <div className="mt-3 min-w-0 sm:ml-20 sm:mt-1 lg:ml-24">
            <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400 sm:text-[11px]">
              Applied for
            </p>

            <p
              className="mt-1 whitespace-nowrap text-[11px] leading-relaxed text-slate-700 sm:text-sm"
              title={`${candidateData.position || "Position not specified"}${candidateData.department
                  ? ` in ${candidateData.department}`
                  : ""
                }`}
            >
              <span className="font-semibold text-slate-900">
                {candidateData.position || "Position not specified"}
              </span>

              {candidateData.department && (
                <span className="text-slate-500">
                  {" "}in {candidateData.department}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Candidate details: two columns on mobile */}
        <div className="mt-4 grid min-w-0 grid-cols-2 gap-x-3 gap-y-4 border-t border-slate-100 pt-4 sm:gap-x-5 sm:gap-y-4 lg:gap-x-8">
          {details.map(({ label, value, Icon, breakAll }) => (
            <div
              key={label}
              className="flex min-w-0 items-start gap-2 sm:gap-3"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500 sm:h-9 sm:w-9 sm:rounded-xl">
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-medium uppercase tracking-wider text-slate-400 sm:text-[11px]">
                  {label}
                </p>

                <p
                  className={`mt-0.5 text-[10px] font-medium leading-relaxed text-slate-700 sm:text-sm ${breakAll ? "break-all" : "break-words"
                    }`}
                >
                  {value || "Not provided"}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* SH-22: Hiring Decision Actions */}
        {(canAccessHiringDecisions || canShowGlobalBlacklist) && (
          <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-4 sm:flex sm:flex-wrap sm:items-center">
            {canAccessHiringDecisions && showNextAction && (
              <Button
                type="button"
                size="sm"
                className="h-9 w-full gap-1.5 rounded-xl px-2 text-xs sm:w-auto sm:gap-2 sm:px-3 sm:text-sm"
                disabled={
                  statusUpdating ||
                  currentStatus !== "Under Review" ||
                  !nextStatus
                }
                onClick={handleNextStatus}
              >
                <UserCheck className="h-4 w-4 shrink-0" />
                {nextStatusLabel}
              </Button>
            )}

            {canAccessHiringDecisions && (
              <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-full gap-1.5 rounded-xl px-2 text-xs sm:w-auto sm:gap-2 sm:px-3 sm:text-sm"
              onClick={onReject}
              disabled={
                statusUpdating ||
                currentStatus === "Rejected" ||
                currentStatus === "Selected" ||
                currentStatus === "Blacklisted"
              }
            >
              <XCircle className="h-4 w-4 shrink-0" />
              Reject
              </Button>
            )}

            {canShowGlobalBlacklist && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-full gap-1.5 rounded-xl border-red-200 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 sm:w-auto sm:gap-2 sm:px-3 sm:text-sm"
              onClick={onBlacklist}
              disabled={
                statusUpdating ||
                currentStatus === "Blacklisted"
              }
            >
              <Ban className="h-4 w-4 shrink-0" />
              Blacklist
            </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
